import { Component, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';

interface UsuarioAtivo {
  id: number;
  idPacienteParam?: string;
  sobrenome: string;
  nome: string;
  cpf: string;
  contato: string;
  email: string;
  perfilAcesso: string;
  dataCadastro: Date | null;
  ativo?: boolean;
  dadosOriginais?: any;
}

type CampoOrdenacaoUsuario = 'sobrenome' | 'nome' | 'cpf' | 'contato' | 'email' | 'perfilAcesso' | 'dataCadastro';

@Component({
  selector: 'app-usuarios-ativos',
  templateUrl: './usuarios-ativos.component.html',
  styleUrls: ['./usuarios-ativos.component.scss']
})
export class UsuariosAtivosComponent implements OnInit {

  // Formulário
  formUsuario: FormGroup;

  // Lista de usuários ativos
  usuariosAtivos: UsuarioAtivo[] = [];
  usuariosFiltrados: UsuarioAtivo[] = [];
  usuariosPaginados: UsuarioAtivo[] = [];
  loading = false;
  paginaAtual = 1;
  itensPorPagina = 10;
  totalPaginas = 0;

  // Ordenação (padrão: cadastro mais recente primeiro)
  ordenacaoCampo: CampoOrdenacaoUsuario = 'dataCadastro';
  ordenacaoDirecao: 'asc' | 'desc' = 'desc';

  // Filtros
  filtroSobrenome: string = '';
  filtroNome: string = '';
  filtroCpf: string = '';
  filtroEmail: string = '';
  filtroPerfil: string = '';

  // Modal
  modalRef: NgbModalRef;
  usuarioSelecionado: UsuarioAtivo | null = null;

  // Opções para selects
  perfisAcesso = [
    'Administrador',
    'Gerente',
    'Operador',
    'Visualizador',
    'Cliente'
  ];



  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private ajaxService: AjaxService,
    private util: UtilService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.inicializarFormulario();
    this.carregarUsuariosAtivos();
  }

  // Inicializar formulário
  inicializarFormulario(): void {
    this.formUsuario = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(2)]],
      cpf: ['', [Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/)]],
      contato: ['', [Validators.required, Validators.pattern(/^\(\d{2}\) \d{5}-\d{4}$/)]],
      email: ['', [Validators.required, Validators.email]],
      perfilAcesso: ['', Validators.required]
    });
  }

  // Baseado na tela antiga: /paciente/_consulta
  // Endpoint equivalente no novo front: api/empresa/Paciente/ConsultarClienteUsuario
  carregarUsuariosAtivos(): void {
    const url = API + 'empresa/Paciente/ConsultarClienteUsuario?ativo=true';
    this.loading = true;
    this.util.showLoading();

    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();

        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.usuariosAtivos = lista
          .filter((item: any) => item && typeof item === 'object')
          .map((item: any) => this.util.converterParaLowercase(item))
          .filter((item: any) => this.isUsuarioAtivo(item))
          .map((item: any) => this.mapearUsuarioAtivo(item));

        this.usuariosFiltrados = [...this.usuariosAtivos];
        this.sincronizarPerfisAcesso();
        this.aplicarFiltros();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.usuariosAtivos = [];
        this.usuariosFiltrados = [];
        this.usuariosPaginados = [];
        this.totalPaginas = 0;
        this.util.toasterShowError('Erro', 'Erro ao carregar usuários ativos');
      }
    });
  }

  // Abrir modal
  openModal(content: TemplateRef<any>, isNew: boolean = false): void {
    if (isNew) {
      this.usuarioSelecionado = null;
      this.limparFormulario();
    }
    this.modalRef = this.modalService.open(content, { size: 'lg' });
  }

  // Salvar usuário
  onSubmit(): void {
    if (this.formUsuario.valid) {
      if (this.usuarioSelecionado) {
        // Editar usuário existente
        const index = this.usuariosAtivos.findIndex(u => u.id === this.usuarioSelecionado!.id);
        if (index !== -1) {
          this.usuariosAtivos[index] = {
            ...this.usuarioSelecionado,
            ...this.formUsuario.value
          };
        }
        console.log('Usuário editado:', this.usuariosAtivos[index]);
        alert('Usuário editado com sucesso!');
      } else {
        // Novo usuário
        const novoUsuario: UsuarioAtivo = {
          id: this.usuariosAtivos.length + 1,
          ...this.formUsuario.value,
          dataCadastro: new Date()
        };
        this.usuariosAtivos.push(novoUsuario);
        console.log('Usuário salvo:', novoUsuario);
        alert('Usuário cadastrado com sucesso!');
      }
      
      this.aplicarFiltros();
      this.modalRef.close();
    } else {
      this.marcarCamposInvalidos();
      alert('Por favor, preencha todos os campos obrigatórios corretamente.');
    }
  }

  // Limpar formulário
  limparFormulario(): void {
    this.formUsuario.reset({
      perfilAcesso: ''
    });
  }

  // Marcar campos inválidos
  marcarCamposInvalidos(): void {
    Object.keys(this.formUsuario.controls).forEach(key => {
      const control = this.formUsuario.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  // Aplicar filtros
  aplicarFiltros(): void {
    this.usuariosFiltrados = this.usuariosAtivos.filter(usuario => {
      const matchSobrenome = !this.filtroSobrenome ||
        (usuario.sobrenome || '').toLowerCase().includes(this.filtroSobrenome.toLowerCase());
      const matchNome = !this.filtroNome ||
        (usuario.nome || '').toLowerCase().includes(this.filtroNome.toLowerCase());
      const matchCpf = !this.filtroCpf ||
        usuario.cpf.includes(this.filtroCpf);
      const matchEmail = !this.filtroEmail ||
        usuario.email.toLowerCase().includes(this.filtroEmail.toLowerCase());
      const matchPerfil = !this.filtroPerfil ||
        usuario.perfilAcesso.toLowerCase() === this.filtroPerfil.toLowerCase();

      return matchSobrenome && matchNome && matchCpf && matchEmail && matchPerfil;
    });
    this.aplicarOrdenacao();
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  ordenarPor(campo: CampoOrdenacaoUsuario): void {
    if (this.ordenacaoCampo === campo) {
      this.ordenacaoDirecao = this.ordenacaoDirecao === 'asc' ? 'desc' : 'asc';
    } else {
      this.ordenacaoCampo = campo;
      this.ordenacaoDirecao = campo === 'dataCadastro' ? 'desc' : 'asc';
    }
    this.aplicarOrdenacao();
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  iconeOrdenacao(campo: CampoOrdenacaoUsuario): string {
    if (this.ordenacaoCampo !== campo) return 'fa-sort';
    return this.ordenacaoDirecao === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  private aplicarOrdenacao(): void {
    const dir = this.ordenacaoDirecao === 'asc' ? 1 : -1;
    const campo = this.ordenacaoCampo;

    this.usuariosFiltrados = [...this.usuariosFiltrados].sort((a, b) => {
      if (campo === 'dataCadastro') {
        const ta = a.dataCadastro ? a.dataCadastro.getTime() : 0;
        const tb = b.dataCadastro ? b.dataCadastro.getTime() : 0;
        return (ta - tb) * dir;
      }

      const va = this.toText((a as any)[campo]).toLowerCase();
      const vb = this.toText((b as any)[campo]).toLowerCase();
      return va.localeCompare(vb, 'pt-BR') * dir;
    });
  }

  // Limpar filtros
  limparFiltros(): void {
    this.filtroSobrenome = '';
    this.filtroNome = '';
    this.filtroCpf = '';
    this.filtroEmail = '';
    this.filtroPerfil = '';
    this.usuariosFiltrados = [...this.usuariosAtivos];
    this.aplicarOrdenacao();
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  // Editar usuário
  editarUsuario(usuario: UsuarioAtivo): void {
    console.log('Editar usuário:', usuario);
    this.usuarioSelecionado = usuario;

    const idPaciente = this.toText(usuario.idPacienteParam) || String(usuario.id || '');
    const clienteData = this.mapearParaClienteCadastro(usuario);

    this.router.navigate(['/cadastro-cliente'], {
      queryParams: {
        modo: 'edicao',
        idPaciente: idPaciente,
        tab: 'servicos',
        clienteData: JSON.stringify(clienteData)
      }
    });
  }

  // Excluir usuário
  excluirUsuario(usuario: UsuarioAtivo): void {
    console.log('Excluir usuário:', usuario);
    if (confirm(`Tem certeza que deseja excluir o usuário ${usuario.nome}?`)) {
      this.usuariosAtivos = this.usuariosAtivos.filter(u => u.id !== usuario.id);
      this.aplicarFiltros();
      alert('Usuário excluído com sucesso!');
    }
  }

  atualizarPaginacao(): void {
    const totalItens = this.usuariosFiltrados.length;
    this.totalPaginas = Math.ceil(totalItens / this.itensPorPagina);

    if (this.totalPaginas === 0) {
      this.paginaAtual = 1;
      this.usuariosPaginados = [];
      return;
    }

    if (this.paginaAtual > this.totalPaginas) {
      this.paginaAtual = this.totalPaginas;
    }

    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.usuariosPaginados = this.usuariosFiltrados.slice(inicio, fim);
  }

  paginaAnterior(): void {
    if (this.paginaAtual <= 1) return;
    this.paginaAtual--;
    this.atualizarPaginacao();
  }

  proximaPagina(): void {
    if (this.paginaAtual >= this.totalPaginas) return;
    this.paginaAtual++;
    this.atualizarPaginacao();
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }



  // Obter classe CSS para perfil
  getPerfilClass(perfil: string): string {
    switch (perfil.toLowerCase()) {
      case 'administrador': return 'badge bg-danger';
      case 'gerente': return 'badge bg-warning';
      case 'operador': return 'badge bg-primary';
      case 'visualizador': return 'badge bg-info';
      case 'cliente': return 'badge bg-success';
      default: return 'badge bg-light text-dark';
    }
  }



  // Formatar CPF
  formatarCPF(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    event.target.value = value;
  }

  // Formatar contato
  formatarContato(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
    value = value.replace(/(\d)(\d{4})$/, '$1-$2');
    event.target.value = value;
  }

  // Métodos auxiliares para verificar erros no template
  isFieldInvalid(fieldName: string): boolean {
    const control = this.formUsuario.get(fieldName);
    return control?.invalid && control?.touched;
  }

  getFieldError(fieldName: string): string {
    const control = this.formUsuario.get(fieldName);
    if (control?.errors) {
      if (control.errors['required']) return 'Campo obrigatório';
      if (control.errors['email']) return 'Email inválido';
      if (control.errors['minlength']) return `Mínimo ${control.errors['minlength'].requiredLength} caracteres`;
      if (control.errors['pattern']) return 'Formato inválido';
    }
    return '';
  }

  private mapearUsuarioAtivo(item: any): UsuarioAtivo {
    const nome = this.toText(item.nome);
    const sobrenome = this.toText(item.sobrenome);
    const telefone = this.toText(item.telefone_celular || item.telefone_residencial || item.telefone_adicional);

    return {
      id: Number(item.id_paciente_clinica || item.id || 0),
      idPacienteParam: this.toText(item.idencrypted || item.id || item.id_paciente_clinica),
      sobrenome,
      nome: nome || 'Sem nome',
      cpf: this.toText(item.cpf),
      contato: telefone,
      email: this.toText(item.email),
      perfilAcesso: this.toText(item.perfilacesso || item.perfil || 'Cliente'),
      dataCadastro: item.data_cadastro ? new Date(item.data_cadastro) : null,
      ativo: this.parseBoolean(item.ativo),
      dadosOriginais: item
    };
  }

  private mapearParaClienteCadastro(usuario: UsuarioAtivo): any {
    const origem = usuario.dadosOriginais || {};
    return {
      id: usuario.id,
      id_paciente_clinica: Number(origem.id_paciente_clinica || usuario.id || 0),
      id_empresa: Number(origem.id_empresa || 1),
      id_usuario: Number(origem.id_usuario || 0),
      id_pais: Number(origem.id_pais || 0),
      id_estado: Number(origem.id_estado || 0),
      id_cidade: Number(origem.id_cidade || 0),
      nome: this.toText(origem.nome) || usuario.nome,
      sobrenome: this.toText(origem.sobrenome) || usuario.sobrenome,
      carteira: this.toText(origem.carteira),
      dante_causa: this.toText(origem.dante_causa || origem.dantecausa),
      cpf: this.toText(origem.cpf) || usuario.cpf,
      email: this.toText(origem.email) || usuario.email,
      telefone_celular: this.toText(origem.telefone_celular || origem.telefone_residencial || origem.telefone_adicional || usuario.contato),
      telefone_adicional: this.toText(origem.telefone_adicional),
      rua: this.toText(origem.rua),
      numero: this.toText(origem.numero),
      complemento: this.toText(origem.complemento),
      bairro: this.toText(origem.bairro),
      cep: this.toText(origem.cep),
      pais: this.toText(origem.pais),
      estado: this.toText(origem.estado),
      cidade: this.toText(origem.cidade),
      link_pasta: this.toText(origem.link_pasta),
      link_pasta_virtual: this.toText(origem.link_pasta || origem.linkpastavirtual),
      pasta_fisica: this.parseBoolean(origem.pasta_fisica),
      observacoes: this.toText(origem.observacoes)
    };
  }

  private isUsuarioAtivo(item: any): boolean {
    if (!item || typeof item !== 'object') return false;

    if (item.ativo === undefined || item.ativo === null) {
      // Se não vier flag de ativo, mantemos para não perder dados.
      return true;
    }

    return this.parseBoolean(item.ativo);
  }

  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;

    const text = this.toText(value).toLowerCase();
    return text === '1' || text === 'true' || text === 'sim' || text === 'ativo';
  }

  private sincronizarPerfisAcesso(): void {
    const perfis = Array.from(
      new Set(
        this.usuariosAtivos
          .map((u) => this.toText(u.perfilAcesso))
          .filter((p) => !!p)
      )
    );

    if (perfis.length > 0) {
      this.perfisAcesso = perfis;
    }
    this.atualizarPaginacao();
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }



} 